import React, { ReactNode, forwardRef, useContext, useEffect, RefObject, ReactElement } from 'react';
import classNames from 'classnames';

import {
  Comp,
  Flexbox,
  FocusWithinGroupContext,
  Item,
  ListboxContext,
  StyledItem,
  isComponent,
  useListboxItem,
  useTheme,
} from '@redsift/design-system';
import { ComboboxContentListboxProps } from './types';
import { ComboboxContext } from '../combobox/context';
import { ComboboxSelectionMode, ComboboxVariant } from '../combobox/types';

const COMPONENT_NAME = 'ComboboxContentListbox';
const CLASSNAME = 'redsift-combobox-content-listbox';

/**
 * The ComboboxContentListbox component.
 */
export const ComboboxContentListbox: Comp<ComboboxContentListboxProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, renderFreeTextItem, ...forwardedProps } = props;

  const focusContext = useContext(FocusWithinGroupContext);
  const listboxState = useContext(ListboxContext);
  const comboboxState = useContext(ComboboxContext);

  useEffect(() => {
    if (focusContext && focusContext.state.delayedAction && focusContext.state.tabStops.length) {
      focusContext.dispatch(focusContext.state.delayedAction);
    }
  });

  const canHaveMultipleSelections = comboboxState?.selectionMode === ComboboxSelectionMode.multiple;
  const canHaveFreeText = comboboxState?.variant === ComboboxVariant.suggestions;

  const cloneChildAndPush = (child: ReactElement, array: Array<ReactNode>) => {
    if (
      !canHaveMultipleSelections ||
      !canHaveFreeText ||
      (canHaveMultipleSelections && canHaveFreeText && !(comboboxState.value as string[]).includes(child.props.value))
    ) {
      array.push(
        React.cloneElement(child, {
          key: child.props.value,
          hasCheckbox: canHaveMultipleSelections && !canHaveFreeText,
        })
      );
    }
  };
  const renderedChildren: Array<ReactNode> = [];
  if (Array.isArray(children)) {
    for (const child of children) {
      if (isComponent(Item)(child)) {
        if (focusContext.state.maxOptionsLength) {
          if (renderedChildren.length < focusContext.state.maxOptionsLength) {
            if (!focusContext.state.filter) {
              cloneChildAndPush(child, renderedChildren);
            } else {
              const { value: filterValue, type, caseSensitive } = focusContext.state.filter;

              if (!filterValue) {
                cloneChildAndPush(child, renderedChildren);
              } else if (
                caseSensitive &&
                ((type === 'startsWith' && child.props.value!.startsWith(filterValue)) ||
                  (type === 'contains' && child.props.value!.includes(filterValue)) ||
                  (type === 'endsWith' && child.props.value!.endsWith(filterValue)))
              ) {
                cloneChildAndPush(child, renderedChildren);
              } else if (
                !caseSensitive &&
                ((type === 'startsWith' && child.props.value!.toLowerCase().startsWith(filterValue.toLowerCase())) ||
                  (type === 'contains' && child.props.value!.toLowerCase().includes(filterValue.toLowerCase())) ||
                  (type === 'endsWith' && child.props.value!.toLowerCase().endsWith(filterValue.toLowerCase())))
              ) {
                cloneChildAndPush(child, renderedChildren);
              }
            }
          }
        } else {
          cloneChildAndPush(child, renderedChildren);
        }
      } else {
        renderedChildren.push(child);
      }
    }
  }

  const { tabIndex, isFocused, isSelected, isDisabled, handleKeyDown, handleClick } = useListboxItem({
    domElementRef: comboboxState?.freeTextItemRef as RefObject<Element>,
    value: comboboxState?.inputValue || '',
    id: comboboxState?.freeTextItemId,
  });
  const theme = useTheme();

  const renderedFreeTextItem = comboboxState?.inputValue
    ? renderFreeTextItem && typeof renderFreeTextItem === 'function'
      ? renderFreeTextItem(comboboxState?.inputValue, comboboxState, listboxState)
      : comboboxState?.inputValue
    : null;

  return (
    <Flexbox
      className={classNames(ComboboxContentListbox.className, className)}
      {...forwardedProps}
      ref={ref}
      flexDirection="column"
      gap="0px"
      margin="8px 0px"
      style={{ outline: 'none' }}
      width="100%"
    >
      {canHaveMultipleSelections && canHaveFreeText ? (
        <StyledItem
          role="option"
          id={comboboxState.freeTextItemId}
          $borderRadius="0"
          $color="primary"
          $isActive={isSelected}
          $isDisabled={isDisabled}
          $isGradient={false}
          $isHovered={isFocused}
          $hasBorder={false}
          $hasCheckbox={false}
          $theme={theme}
          aria-selected={isSelected}
          className={classNames(Item.className, className)}
          onClick={handleClick}
          ref={comboboxState.freeTextItemRef as RefObject<HTMLDivElement>}
          onKeyDown={handleKeyDown}
          tabIndex={tabIndex}
          style={
            !comboboxState.inputValue
              ? {
                  height: '0',
                  visibility: 'hidden',
                  padding: '0',
                }
              : undefined
          }
        >
          {renderedFreeTextItem}
        </StyledItem>
      ) : null}
      {Array.isArray(children) ? renderedChildren : children}
    </Flexbox>
  );
});
ComboboxContentListbox.className = CLASSNAME;
ComboboxContentListbox.displayName = COMPONENT_NAME;
